import type { MD3Size } from "../../../types/md3";
import { BUTTON_GROUP_TOKENS } from "./button-group-tokens";

/** Pill radius (height / 2) per size. */
export const PILL_RADIUS_MAP: Record<MD3Size, number> = {
	xs: 16,
	sm: 20,
	md: 28,
	lg: 48,
	xl: 68,
};

/** Morphing target radius when a standard button is pressed. */
export const PRESSED_RADIUS_MAP: Record<MD3Size, number> = {
	xs: 8,
	sm: 10,
	md: 16,
	lg: 28,
	xl: 40,
};

export interface Corners {
	tl: number;
	tr: number;
	br: number;
	bl: number;
}

export interface ShapeSet {
	default: Corners;
	pressed: Corners;
	checked: Corners;
}

const PILL_CORNERS = {
	tl: 9999,
	tr: 9999,
	br: 9999,
	bl: 9999,
} as const satisfies Corners;

function buildConnectedShapes(
	size: MD3Size,
	defaultCorners: (outer: number, inner: number) => Corners,
	pressedCorners: (outer: number, pressedInner: number) => Corners,
): ShapeSet {
	const outer = PILL_RADIUS_MAP[size];
	const inner = BUTTON_GROUP_TOKENS.connected.innerCorner;
	const pressedInner = BUTTON_GROUP_TOKENS.connected.pressedInnerCorner;
	return {
		default: defaultCorners(outer, inner),
		pressed: pressedCorners(outer, pressedInner),
		checked: { ...PILL_CORNERS },
	};
}

export const ButtonGroupDefaults = {
	connectedLeadingButtonShapes: (size: MD3Size): ShapeSet =>
		buildConnectedShapes(
			size,
			(o, i) => ({ tl: o, tr: i, br: i, bl: o }),
			(o, pi) => ({ tl: o, tr: pi, br: pi, bl: o }),
		),

	connectedMiddleButtonShapes: (size: MD3Size): ShapeSet =>
		buildConnectedShapes(
			size,
			(_o, i) => ({ tl: i, tr: i, br: i, bl: i }),
			(_o, pi) => ({ tl: pi, tr: pi, br: pi, bl: pi }),
		),

	connectedTrailingButtonShapes: (size: MD3Size): ShapeSet =>
		buildConnectedShapes(
			size,
			(o, i) => ({ tl: i, tr: o, br: o, bl: i }),
			(o, pi) => ({ tl: pi, tr: o, br: o, bl: pi }),
		),

	/** For orientation="vertical". */
	connectedTopButtonShapes: (size: MD3Size): ShapeSet =>
		buildConnectedShapes(
			size,
			(o, i) => ({ tl: o, tr: o, br: i, bl: i }),
			(o, pi) => ({ tl: o, tr: o, br: pi, bl: pi }),
		),

	connectedBottomButtonShapes: (size: MD3Size): ShapeSet =>
		buildConnectedShapes(
			size,
			(o, i) => ({ tl: i, tr: i, br: o, bl: o }),
			(o, pi) => ({ tl: pi, tr: pi, br: o, bl: o }),
		),
};
